import type { LucideIcon } from 'lucide-react';
import { PeopleIcon } from '../components/icons';
import {
  CalendarClock,
  UserCheck,
  Vote,
  Landmark,
  Mail,
  Send,
  Megaphone,
  MessageSquare,
  Database,
  Rss,
  Wallet,
} from 'lucide-react';

/**
 * Single source of truth for the new admin suite.
 *
 * Every tool in the suite is declared here once; the dashboard cards, the
 * sidebar navigation, and the legacy-tool stub pages are all generated from
 * this registry. Adding a tool = adding an entry here (plus its page and
 * route). A tool cannot exist without being navigable.
 *
 * `status` drives the rollout:
 * - 'ready'       — fully rebuilt in the new suite
 * - 'in-progress' — page exists in the new suite; some views still link to
 *                   the legacy tool
 * - 'legacy'      — not rebuilt yet; the generic stub page links to the
 *                   legacy tool(s) it will replace
 */
export type AdminToolStatus = 'ready' | 'in-progress' | 'legacy';

export interface LegacyLink {
  label: string;
  path: string;
}

export interface AdminTool {
  /** Route segment under ADMIN_NEXT_BASE, e.g. 'accounts' → /admin-next/accounts */
  key: string;
  title: string;
  description: string;
  icon: LucideIcon;
  status: AdminToolStatus;
  /** Names of the legacy tools this tool consolidates/replaces */
  consolidates: string[];
  /** Where the legacy equivalents live, for stubs and cutover tracking */
  legacyLinks: LegacyLink[];
}

export interface AdminToolGroup {
  key: string;
  title: string;
  tools: AdminTool[];
}

export const ADMIN_NEXT_BASE = '/admin-next';

export const ADMIN_TOOL_GROUPS: AdminToolGroup[] = [
  {
    key: 'accounts-access',
    title: 'Accounts & Access',
    tools: [
      {
        key: 'accounts',
        title: 'Accounts',
        description:
          'Users, organizations, and workspaces in one connected view — who has access to what, and how active they are.',
        icon: PeopleIcon,
        status: 'ready',
        consolidates: [
          'User Management',
          'Organization Management',
          'Workspace Management',
          'User Analytics',
        ],
        legacyLinks: [],
      },
    ],
  },
  {
    key: 'civic-data',
    title: 'Civic Data',
    tools: [
      {
        key: 'people',
        title: 'People',
        description:
          'Every person in the registry — candidates, legislators, and officeholders — with profiles, committees, photos, and duplicate merging.',
        icon: UserCheck,
        status: 'ready',
        consolidates: ['Candidate / Legislator Management'],
        legacyLinks: [],
      },
      {
        key: 'committees',
        title: 'Committees',
        description:
          'Every campaign committee in the TRACER campaign finance import — purpose statements and the other details the import does not carry, shared by every measure and candidate linked to it.',
        icon: Wallet,
        status: 'ready',
        consolidates: [],
        legacyLinks: [],
      },
      {
        key: 'calendar',
        title: 'Calendar',
        description:
          'Authored events across sources, the collection source registry, and the daily CSV import.',
        icon: CalendarClock,
        status: 'ready',
        consolidates: [],
        legacyLinks: [],
      },
      {
        key: 'elections',
        title: 'Elections',
        description:
          'Election cycles, the shared office catalog, contests, and ballot initiatives.',
        icon: Vote,
        status: 'ready',
        consolidates: ['Election Cycles', 'Offices', 'Ballot Initiatives'],
        legacyLinks: [],
      },
      {
        key: 'legislature',
        title: 'Legislature',
        description:
          'Legislative sessions — convene and adjourn dates decide which session the whole app treats as current.',
        icon: Landmark,
        status: 'ready',
        consolidates: [],
        legacyLinks: [],
      },
    ],
  },
  {
    key: 'communications',
    title: 'Communications',
    tools: [
      {
        key: 'templates',
        title: 'Templates',
        description:
          'Email and outreach templates as one library — different types, same editor, preview, and versioning.',
        icon: Mail,
        status: 'legacy',
        consolidates: ['Email Templates', 'Outreach Templates'],
        legacyLinks: [
          { label: 'Legacy Email Templates', path: '/admin/email-templates' },
          { label: 'Legacy Outreach Templates', path: '/admin/outreach-templates' },
        ],
      },
      {
        key: 'digest-emails',
        title: 'Digest Emails',
        description:
          'Calendar digest email jobs — scraper status, sends, subscriber counts, and job history.',
        icon: Send,
        status: 'legacy',
        consolidates: ['Calendar Emails'],
        legacyLinks: [
          { label: 'Legacy Calendar Emails', path: '/admin/calendar-emails' },
        ],
      },
      {
        key: 'announcements',
        title: 'Announcements',
        description:
          'In-app announcements to client organizations, and the audience groups they are sent to.',
        icon: Megaphone,
        status: 'legacy',
        consolidates: ['Client Announcements', 'Audience Groups'],
        legacyLinks: [
          { label: 'Legacy Announcements', path: '/admin/announcements' },
          { label: 'Legacy Audience Groups', path: '/admin/audience-groups' },
        ],
      },
      {
        key: 'news-feeds',
        title: 'News Feeds',
        description:
          'The Colorado news sources behind the dashboard news widget — turn feeds on and off, test them, and add your own.',
        icon: Rss,
        status: 'ready',
        consolidates: [],
        legacyLinks: [],
      },
    ],
  },
  {
    key: 'citizen',
    title: 'Citizen',
    tools: [
      {
        key: 'engagement-prompts',
        title: 'Engagement Prompts',
        description:
          'The human-curated micro-engagement bank served to citizens — author, review against the neutrality checklist, and publish.',
        icon: MessageSquare,
        status: 'ready',
        consolidates: [],
        legacyLinks: [],
      },
    ],
  },
  {
    key: 'platform',
    title: 'Platform',
    tools: [
      {
        key: 'data-import',
        title: 'Data Import',
        description:
          'Bill, campaign-finance, and reference-data imports, district maps, and import job monitoring.',
        icon: Database,
        status: 'legacy',
        consolidates: ['Data Import'],
        legacyLinks: [{ label: 'Legacy Data Import', path: '/admin/data-import' }],
      },
    ],
  },
];

export const ALL_ADMIN_TOOLS: AdminTool[] = ADMIN_TOOL_GROUPS.flatMap(
  (group) => group.tools
);

export const findAdminTool = (key: string): AdminTool | undefined =>
  ALL_ADMIN_TOOLS.find((tool) => tool.key === key);

export const adminToolPath = (tool: AdminTool): string =>
  `${ADMIN_NEXT_BASE}/${tool.key}`;
